import { create } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'
import { format } from 'date-fns'
import type { DatePickerRangeProps } from '@/modules/drivers/types'
import {
  applyMonth,
  applyRange,
  applySingle,
  applyYear,
  formatApiDate,
  resolveDateRange,
} from './dateStore.utils'
import { zustandPlugin } from '@/modules/shared/openreplay'

type View = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'details'

type State = {
  single: string | null
  range: DatePickerRangeProps | null
  month: string | null
  year: string | null
}

type Actions = {
  setSingle: (d: string | null) => void
  setRange: (d: DatePickerRangeProps | null) => void
  setMonth: (d: string | null) => void
  setYear: (d: string | null) => void
  formatApiDate: (date: Date) => string
  getDateForView: (targetView: View) => string
  getDateRangeForView: () => { from: string; to: string }
}

export const useDateStore = create<State & Actions>()(
  devtools(
    persist(
      zustandPlugin((set, get) => ({
        single: null,
        range: null,
        month: null,
        year: null,

        setSingle: (d) => set((s) => applySingle(s, d), false, 'setSingle'),

        setRange: (d) => set((s) => applyRange(s, d), false, 'setRange'),

        setMonth: (d) => set((s) => applyMonth(s, d), false, 'setMonth'),

        setYear: (d) => set((s) => applyYear(s, d), false, 'setYear'),

        formatApiDate,

        getDateForView: (targetView) => {
          const { single, month, year } = get()
          const now = new Date()
          switch (targetView) {
            case 'daily':
            case 'weekly':
            case 'details': {
              if (single) return single
              if (month) return `${month}-01`
              if (year) return `${year}-01-01`
              return format(now, 'YYYY-MM-DD')
            }
            case 'monthly': {
              if (month) return month
              if (single) return single.substring(0, 7)
              if (year) return `${year}-01`
              return format(now, 'YYYY-MM')
            }
            case 'yearly': {
              if (year) return year
              if (month) return month.substring(0, 4)
              if (single) return single.substring(0, 4)
              return format(now, 'YYYY')
            }
            default:
              return format(now, 'YYYY-MM-DD')
          }
        },

        getDateRangeForView: () => resolveDateRange(get(), new Date()),
      }), 'date'),
      {
        name: 'driver-date',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (s) => ({
          single: s.single,
          range: s.range,
          month: s.month,
          year: s.year,
        }),
      },
    ),
    { name: 'EasyTacho', store: 'date' },
  ),
)

export const useDate = () =>
  useDateStore(
    useShallow((s) => ({
      single: s.single,
      range: s.range,
      month: s.month,
      year: s.year,
      setSingle: s.setSingle,
      setRange: s.setRange,
      setMonth: s.setMonth,
      setYear: s.setYear,
      formatApiDate: s.formatApiDate,
      getDateForView: s.getDateForView,
      getDateRangeForView: s.getDateRangeForView,
    })),
  )
